import { useTranslation } from "react-i18next";
import type { i18n as I18n, TFunction } from "i18next";

/** The world viewer's strings, a separate namespace so the page shell's messages stay untouched. */
export const worldMessages = {
  en: {
    panel: {
      title: "World details",
      hint: "Click a building to see its floors and who works there.",
      company: "Company",
      floors_one: "{{count}} floor",
      floors_other: "{{count}} floors",
      floorLevel: "Floor {{level}}",
      basement: "Basement {{level}}",
      lead: "Lead",
      noLead: "No lead yet",
      workers_one: "{{count}} worker",
      workers_other: "{{count}} workers",
      noWorkers: "No workers",
      noFloors: "No floors yet",
      close: "Close",
      landmarkTitle: "Uzverse hub",
      landmarkText: "The landmark at the center of the world. Companies are built around it.",
      buildings: "Buildings",
      publicOnly: "Only the public outline of this building is shared with you.",
      pendingTitle: "Waiting for approval.",
      pendingMove: "The owner has to allow moving this building to x {{x}}, z {{z}}.",
      pendingPlace: "The owner has to allow placing this building at x {{x}}, z {{z}}.",
    },
    state: {
      loading: "Loading the world…",
      unavailable: "The live world is not available right now.",
      error: "The building details could not be loaded.",
      retry: "Try again",
      empty: "Empty land. Your first building appears here.",
    },
    construction: { build: "Under construction", demolish: "Being demolished" },
    modes: {
      label: "Camera",
      orbit: "Orbit",
      first_person: "First person",
      third_person: "Third person",
      fly: "Fly",
      blueprint: "Blueprint",
    },
    help: {
      orbit:
        "Drag to turn, right-drag to move, scroll to zoom. Click a building, double-click to go in.",
      first_person:
        "Drag to look, hold the arrows to walk (or WASD, Shift runs). Use the floor list to change floors.",
      third_person:
        "Drag to look, hold the arrows to walk (or WASD, Shift runs). Use the floor list to change floors.",
      fly: "Drag to look, hold the arrows to fly (or WASD, Space up, C down).",
      blueprint:
        "Drag one of your buildings to move it, or click it and press Turn. Green fits, red does not.",
      touch: "Left thumb to move, drag on the right to look.",
    },
    weather: {
      label: "Weather",
      hint: "Auto changes the weather by itself every 20 minutes.",
      auto: "Auto",
      clear: "Clear",
      overcast: "Cloudy",
      rain: "Rain",
      fog: "Fog",
      set_auto: "The weather changes by itself now",
      set_clear: "Clear skies",
      set_overcast: "Clouds are rolling in",
      set_rain: "It is starting to rain",
      set_fog: "Fog is coming in",
    },
    time: {
      label: "Time",
      auto: "Auto",
      day: "Day",
      dusk: "Dusk",
      night: "Night",
      hour: "Hour",
      set: "World time set to {{when}}",
      setAuto: "World time follows your clock",
    },
    game: {
      menu: "App menu",
      backToApp: "Back to the app",
      panel: "Panel",
      panelTitle: "World details (Tab)",
      closePanel: "Close panel",
      fullscreen: "Fullscreen",
      fullscreenTitle: "Fullscreen (F)",
      exitFullscreen: "Exit fullscreen",
      layout: "Layout",
      layoutImmersive: "Immersive",
      layoutDocked: "Docked",
      settings: "Settings",
      sound: "Sounds",
      compass: "Compass",
      minimap: "Minimap",
      minimapHint: "Click the map to fly there",
      loadingTitle: "Entering the world",
      loadingScene: "Building the scene",
      loadingAssets: "Loading assets",
      loadingData: "Reaching the world",
      loadingReady: "Ready",
      helpHint: "H shows help",
      north: "N",
      east: "E",
      south: "S",
      west: "W",
      settingsTitle: "Settings",
      secDisplay: "Display",
      secControls: "Controls",
      secAudio: "Sound",
      secWorld: "World",
      showMinimap: "Minimap",
      showCompass: "Compass",
      showLabels: "Mode labels",
      layoutHint: "Immersive hides the side panel",
      soundHint: "Interface and vehicle sounds",
      lookHint: "Mouse and stick speed",
      invertHint: "Up on the mouse looks down",
      bobHint: "Walking sway in first person",
      timeHint: "Auto follows your clock",
      closeSettings: "Close settings",
      modeRail: "Camera mode",
    },
    nav: {
      enter: "Enter {{name}}",
      enterSelected: "Enter building",
      enterKey: "E",
      membersOnly: "Only members of this company can go inside.",
      cannotEnter: "This building cannot be entered.",
      quality: "Quality",
      qualityAuto: "Auto",
      qualityLow: "Low",
      qualityHigh: "High",
      joystick: "Move",
      jump: "Jump",
      sensitivity: "Look speed",
      invertY: "Invert look",
      headBob: "Head bob",
      pad: "Walk controls",
      forward: "Forward",
      back: "Back",
      turnLeft: "Turn left",
      turnRight: "Turn right",
      up: "Up",
      down: "Down",
      mouseLook: "Mouse look",
      mouseLookHint: "Hides the cursor to look around. Esc gives it back.",
      lockedHint: "Press Esc to get the mouse back.",
      toasts: "Messages",
      dismiss: "Dismiss",
    },
    interior: {
      exit: "Exit",
      exitBuilding: "Exit building",
      elevator: "Elevator",
      floors: "Floors",
      goTo: "Go to floor {{level}}",
      directory: "Directory",
      floorTitle: "Floor {{level}}",
      floorNamed: "{{name}} · Floor {{level}}",
      lead: "Lead",
      worker: "Worker",
      inside: "Inside {{name}}",
      floorUp: "Floor up",
      floorDown: "Floor down",
      wheelHint: "Click a floor: the elevator takes you there. Arrows and scroll work too.",
      peelHint: "Pick a floor to open the building up.",
      cutawayHint: "Click a floor, use the arrows or scroll here.",
      closeCutaway: "Close view",
      liftUse: "Use the elevator",
      liftCall: "Call the elevator",
      liftPick: "Pick a floor",
      liftUp: "Up",
      liftDown: "Down",
      liftOpen: "Open doors",
      liftClose: "Close doors",
      liftHere: "You are here",
      liftCar: "Elevator {{name}}",
      liftClosePanel: "Close",
    },
    blueprint: {
      moved: "Moved {{name}}",
      rotate: "Turn 90°",
      rotateHint: "Click one of your buildings, then turn it a quarter.",
      undo: "Undo",
      undone: "Undone.",
      reason: {
        overlap: "That spot overlaps another building.",
        outside_parcel: "The building has to stay inside your plot.",
        outside_world: "That spot is outside the world.",
      },
      error: {
        conflict: "Could not move it: {{detail}}",
        permission_denied: "You are not allowed to move that building here.",
        invalid_input: "That placement is not valid: {{detail}}",
        not_found: "That building no longer exists.",
        unavailable: "The world service is not reachable. Try again.",
        other: "Could not move it. {{detail}}",
        unconfirmed: "The move was not confirmed, so the building went back.",
      },
    },
    target: {
      title: "Looking at",
      none: "Nothing selected. Click a building or a wall in the world.",
      clear: "Clear",
      unknown: "A part of the world",
      part: {
        wall: { n: "north wall", e: "east wall", s: "south wall", w: "west wall" },
        wallAny: "wall",
        slab: "floor slab",
        roof: "roof",
        body: "body",
      },
    },
    fallback: {
      title: "3D view unavailable",
      text: "Your browser or device cannot show the 3D world right now. The building list still works.",
    },
    canvas: {
      label: "3D world. Drag to orbit, scroll or pinch to zoom, click a building for details.",
    },
  },
  tr: {
    panel: {
      title: "Dünya ayrıntıları",
      hint: "Katlarını ve orada kimin çalıştığını görmek için bir binaya tıklayın.",
      company: "Şirket",
      floors_one: "{{count}} kat",
      floors_other: "{{count}} kat",
      floorLevel: "{{level}}. kat",
      basement: "Bodrum {{level}}",
      lead: "Lider",
      noLead: "Henüz lider yok",
      workers_one: "{{count}} çalışan",
      workers_other: "{{count}} çalışan",
      noWorkers: "Çalışan yok",
      noFloors: "Henüz kat yok",
      close: "Kapat",
      landmarkTitle: "Uzverse merkezi",
      landmarkText: "Dünyanın ortasındaki simge yapı. Şirketler onun çevresine kurulur.",
      buildings: "Binalar",
      publicOnly: "Bu binanın yalnızca herkese açık bilgileri sizinle paylaşılır.",
      pendingTitle: "Onay bekliyor.",
      pendingMove: "Bu binanın x {{x}}, z {{z}} konumuna taşınması için sahibin izni gerekiyor.",
      pendingPlace:
        "Bu binanın x {{x}}, z {{z}} konumuna yerleştirilmesi için sahibin izni gerekiyor.",
    },
    state: {
      loading: "Dünya yükleniyor…",
      unavailable: "Canlı dünya şu anda kullanılamıyor.",
      error: "Bina ayrıntıları yüklenemedi.",
      retry: "Tekrar dene",
      empty: "Boş arazi. İlk binanız burada görünecek.",
    },
    construction: { build: "İnşa ediliyor", demolish: "Yıkılıyor" },
    modes: {
      label: "Kamera",
      orbit: "Yörünge",
      first_person: "Birinci kişi",
      third_person: "Üçüncü kişi",
      fly: "Uçuş",
      blueprint: "Plan",
    },
    help: {
      orbit:
        "Döndürmek için sürükleyin, kaydırmak için sağ tuşla sürükleyin, yakınlaştırmak için tekerleği çevirin. Bir binaya tıklayın, girmek için çift tıklayın.",
      first_person:
        "Bakmak için sürükleyin, yürümek için okları basılı tutun (veya WASD, Shift koşar). Kat değiştirmek için kat listesini kullanın.",
      third_person:
        "Bakmak için sürükleyin, yürümek için okları basılı tutun (veya WASD, Shift koşar). Kat değiştirmek için kat listesini kullanın.",
      fly: "Bakmak için sürükleyin, uçmak için okları basılı tutun (veya WASD, yukarı Boşluk, aşağı C).",
      blueprint:
        "Taşımak için kendi binalarınızdan birini sürükleyin ya da tıklayıp Döndür'e basın. Yeşil olur, kırmızı olmaz.",
      touch: "Sol başparmakla yürüyün, sağ tarafı sürükleyerek bakın.",
    },
    weather: {
      label: "Hava",
      hint: "Otomatikte hava her 20 dakikada kendiliğinden değişir.",
      auto: "Otomatik",
      clear: "Açık",
      overcast: "Bulutlu",
      rain: "Yağmur",
      fog: "Sis",
      set_auto: "Hava artık kendiliğinden değişiyor",
      set_clear: "Gökyüzü açıldı",
      set_overcast: "Bulutlar geliyor",
      set_rain: "Yağmur başlıyor",
      set_fog: "Sis çöküyor",
    },
    time: {
      label: "Zaman",
      auto: "Otomatik",
      day: "Gündüz",
      dusk: "Akşam",
      night: "Gece",
      hour: "Saat",
      set: "Dünya saati {{when}} yapıldı",
      setAuto: "Dünya saati cihaz saatini izliyor",
    },
    game: {
      menu: "Uygulama menüsü",
      backToApp: "Uygulamaya dön",
      panel: "Panel",
      panelTitle: "Dünya ayrıntıları (Tab)",
      closePanel: "Paneli kapat",
      fullscreen: "Tam ekran",
      fullscreenTitle: "Tam ekran (F)",
      exitFullscreen: "Tam ekrandan çık",
      layout: "Düzen",
      layoutImmersive: "Oyun",
      layoutDocked: "Yanda panelli",
      settings: "Ayarlar",
      sound: "Sesler",
      compass: "Pusula",
      minimap: "Harita",
      minimapHint: "Oraya uçmak için haritaya tıklayın",
      loadingTitle: "Dünyaya giriliyor",
      loadingScene: "Sahne kuruluyor",
      loadingAssets: "Varlıklar yükleniyor",
      loadingData: "Dünyaya bağlanılıyor",
      loadingReady: "Hazır",
      helpHint: "H yardımı gösterir",
      north: "K",
      east: "D",
      south: "G",
      west: "B",
      settingsTitle: "Ayarlar",
      secDisplay: "Görüntü",
      secControls: "Kontroller",
      secAudio: "Ses",
      secWorld: "Dünya",
      showMinimap: "Harita",
      showCompass: "Pusula",
      showLabels: "Mod adları",
      layoutHint: "Oyun düzeni yan paneli gizler",
      soundHint: "Arayüz ve araç sesleri",
      lookHint: "Fare ve kol hızı",
      invertHint: "Fareyi yukarı çekince aşağı bakar",
      bobHint: "Birinci kişide yürüyüş sallanması",
      timeHint: "Otomatik, cihaz saatini izler",
      closeSettings: "Ayarları kapat",
      modeRail: "Kamera modu",
    },
    nav: {
      enter: "{{name}} içine gir",
      enterSelected: "Binaya gir",
      enterKey: "E",
      membersOnly: "İçeri yalnızca bu şirketin üyeleri girebilir.",
      cannotEnter: "Bu binaya girilemez.",
      quality: "Kalite",
      qualityAuto: "Otomatik",
      qualityLow: "Düşük",
      qualityHigh: "Yüksek",
      joystick: "Hareket",
      jump: "Zıpla",
      sensitivity: "Bakış hızı",
      invertY: "Bakışı ters çevir",
      headBob: "Baş sallanması",
      pad: "Yürüme düğmeleri",
      forward: "İleri",
      back: "Geri",
      turnLeft: "Sola dön",
      turnRight: "Sağa dön",
      up: "Yukarı",
      down: "Aşağı",
      mouseLook: "Fareyle bak",
      mouseLookHint: "Etrafa bakmak için imleci gizler. Esc imleci geri verir.",
      lockedHint: "Fareyi geri almak için Esc'ye basın.",
      toasts: "Mesajlar",
      dismiss: "Kapat",
    },
    interior: {
      exit: "Çıkış",
      exitBuilding: "Binadan çık",
      elevator: "Asansör",
      floors: "Katlar",
      goTo: "{{level}}. kata git",
      directory: "Kat rehberi",
      floorTitle: "Kat {{level}}",
      floorNamed: "{{name}} · Kat {{level}}",
      lead: "Lider",
      worker: "Çalışan",
      inside: "{{name}} içinde",
      floorUp: "Üst kat",
      floorDown: "Alt kat",
      wheelHint: "Bir kata tıklayın: asansör sizi götürür. Oklar ve kaydırma da çalışır.",
      peelHint: "Binayı açmak için bir kat seçin.",
      cutawayHint: "Bir kata tıklayın, okları kullanın veya burada kaydırın.",
      closeCutaway: "Görünümü kapat",
      liftUse: "Asansörü kullan",
      liftCall: "Asansörü çağır",
      liftPick: "Bir kat seçin",
      liftUp: "Yukarı",
      liftDown: "Aşağı",
      liftOpen: "Kapıyı aç",
      liftClose: "Kapıyı kapat",
      liftHere: "Buradasınız",
      liftCar: "Asansör {{name}}",
      liftClosePanel: "Kapat",
    },
    blueprint: {
      moved: "{{name}} taşındı",
      rotate: "90° döndür",
      rotateHint: "Kendi binanıza tıklayın, sonra çeyrek tur döndürün.",
      undo: "Geri al",
      undone: "Geri alındı.",
      reason: {
        overlap: "Burası başka bir binayla çakışıyor.",
        outside_parcel: "Bina kendi arsanızın içinde kalmalı.",
        outside_world: "Burası dünyanın dışında.",
      },
      error: {
        conflict: "Taşınamadı: {{detail}}",
        permission_denied: "Bu binayı buraya taşıma izniniz yok.",
        invalid_input: "Bu yerleşim geçerli değil: {{detail}}",
        not_found: "Bu bina artık yok.",
        unavailable: "Dünya servisine ulaşılamıyor. Tekrar deneyin.",
        other: "Taşınamadı. {{detail}}",
        unconfirmed: "Taşıma onaylanmadı, bina yerine döndü.",
      },
    },
    target: {
      title: "Baktığınız yer",
      none: "Seçili bir şey yok. Dünyada bir binaya veya duvara tıklayın.",
      clear: "Temizle",
      unknown: "Dünyanın bir parçası",
      part: {
        wall: { n: "kuzey duvarı", e: "doğu duvarı", s: "güney duvarı", w: "batı duvarı" },
        wallAny: "duvar",
        slab: "kat döşemesi",
        roof: "çatı",
        body: "gövde",
      },
    },
    fallback: {
      title: "3B görünüm kullanılamıyor",
      text: "Tarayıcınız veya cihazınız 3B dünyayı şu anda gösteremiyor. Bina listesi çalışmaya devam eder.",
    },
    canvas: {
      label:
        "3B dünya. Döndürmek için sürükleyin, yakınlaştırmak için kaydırın veya sıkıştırın, ayrıntı için bir binaya tıklayın.",
    },
  },
} as const;

export const WORLD_NS = "world";

/** Registers the bundles on the app's i18n instance once (idempotent). */
export function ensureWorldBundles(i18n: I18n): void {
  for (const [lng, bundle] of Object.entries(worldMessages)) {
    if (!i18n.hasResourceBundle(lng, WORLD_NS)) {
      i18n.addResourceBundle(lng, WORLD_NS, bundle, true, false);
    }
  }
}

export function useWorldT(): TFunction<typeof WORLD_NS> {
  const { i18n } = useTranslation();
  ensureWorldBundles(i18n);
  return useTranslation(WORLD_NS).t;
}
